/**
 * Caracteres invisibles : detection et nettoyage.
 *
 * Pourquoi ce fichier : le contenu produit par un modele arrive regulierement
 * avec des caracteres sans largeur (espace de largeur nulle, trait d'union
 * conditionnel, marque bidirectionnelle, caracteres de tag). Ils ne se voient
 * pas, mais ils cassent tout ce qui compare des chaines : la correction d'un
 * exercice Reps, la generation d'un slug, le diff d'un fichier de traduction,
 * une recherche en base. Les masquer au rendu ne suffit pas : ils restent
 * stockes, et l'application mobile ou le back ne passent pas par le rendu web.
 *
 * Deux regles d'admission, aussi importantes l'une que l'autre.
 *
 * 1. LES ESPACES FRANCAISES NE SONT PAS DES PARASITES. L'espace insecable
 *    (U+00A0) et l'espace fine insecable (U+202F) portent la typographie
 *    francaise : les guillemets et l'espace devant ; : ! ?. Rien ici ne les
 *    touche, et aucune normalisation d'espaces n'est faite nulle part. Les
 *    espaces typographiques U+2000-U+200A sont signalees par la detection
 *    mais jamais retirees, pour la meme raison : remplacer une espace par une
 *    autre est un arbitrage de mise en page, pas un nettoyage.
 *
 * 2. LES EMOJI COMPOSES NE SONT PAS DES PARASITES. Le liant U+200D assemble
 *    les emoji multi-personnages, et le selecteur U+FE0F fait d'un symbole un
 *    emoji couleur plutot qu'un glyphe texte noir. Les retirer casse le
 *    contenu au lieu de le nettoyer.
 */

// Plages retirees sans condition. Chaque entree : [premier, dernier, nom].
// Volontairement absents : U+00A0, U+202F (regle 1), U+2000-U+200A (regle 1),
// U+200D (conditionnel, regle 2), U+FE00-U+FE0F (regle 2).
const STRIPPED_RANGES = [
    [0x00ad, 0x00ad, "SOFT HYPHEN"],
    [0x034f, 0x034f, "COMBINING GRAPHEME JOINER"],
    [0x061c, 0x061c, "ARABIC LETTER MARK"],
    [0x115f, 0x1160, "HANGUL FILLER"],
    [0x17b4, 0x17b5, "KHMER VOWEL INHERENT"],
    [0x180b, 0x180e, "MONGOLIAN SELECTOR / VOWEL SEPARATOR"],
    [0x200b, 0x200c, "ZERO WIDTH SPACE / NON-JOINER"],
    [0x200e, 0x200f, "LEFT-TO-RIGHT / RIGHT-TO-LEFT MARK"],
    [0x2028, 0x2029, "LINE / PARAGRAPH SEPARATOR"],
    [0x202a, 0x202e, "BIDI EMBEDDING / OVERRIDE"],
    [0x2060, 0x2064, "WORD JOINER / INVISIBLE OPERATOR"],
    [0x2066, 0x2069, "BIDI ISOLATE"],
    [0xfeff, 0xfeff, "ZERO WIDTH NO-BREAK SPACE (BOM)"],
    [0xfff9, 0xfffc, "INTERLINEAR ANNOTATION / OBJECT REPLACEMENT"],
    // Hors du plan multilingue de base. Ces deux plages sont le vecteur
    // moderne de contrebande de texte : elles encodent de l'ASCII entier de
    // facon totalement invisible. L'ancienne expression les laissait passer.
    [0xe0000, 0xe007f, "TAG CHARACTER"],
    [0xe0100, 0xe01ef, "VARIATION SELECTOR SUPPLEMENT"],
];

// Espaces typographiques : signalees, jamais retirees (regle 1).
const REPORTED_ONLY_RANGES = [[0x2000, 0x200a, "ESPACE TYPOGRAPHIQUE"]];

const ZWJ = 0x200d;
const VARIATION_SELECTOR_16 = 0xfe0f;

const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;

const escape = (codePoint) => `\\u{${codePoint.toString(16)}}`;

/**
 * Construit une classe de caracteres depuis des codepoints numeriques.
 *
 * Aucune plage n'est ecrite en litteral dans ce fichier : un caractere
 * invisible colle dans le code source serait invisible pour la relecture, et
 * U+2028 casse meme le parsing d'un module.
 */
function buildClass(ranges, extraCodePoints = []) {
    const parts = ranges.map(([first, last]) =>
        first === last ? escape(first) : `${escape(first)}-${escape(last)}`,
    );
    parts.push(...extraCodePoints.map(escape));
    return new RegExp(`[${parts.join("")}]`, "gu");
}

const STRIP_PATTERN = buildClass(STRIPPED_RANGES, [ZWJ]);
const DETECT_PATTERN = buildClass([...STRIPPED_RANGES, ...REPORTED_ONLY_RANGES], [ZWJ]);

/**
 * Codepoint qui precede la position donnee, paires de substitution comprises.
 */
function codePointBefore(text, index) {
    if (index <= 0) return null;
    const previous = text.charCodeAt(index - 1);
    const isLowSurrogate = previous >= 0xdc00 && previous <= 0xdfff;
    return isLowSurrogate && index >= 2 ? text.codePointAt(index - 2) : previous;
}

/**
 * Le liant U+200D assemble-t-il bien deux emoji a cette position ?
 *
 * Un selecteur de variante peut s'intercaler avant le liant (le drapeau de
 * 🏳️‍🌈 en porte un), d'ou le saut.
 */
function joinsPictographic(text, index) {
    let cursor = index;
    let before = codePointBefore(text, cursor);

    if (before === VARIATION_SELECTOR_16) {
        cursor -= 1;
        before = codePointBefore(text, cursor);
    }

    const after = text.codePointAt(index + 1);
    if (before === null || after === undefined) return false;

    return (
        PICTOGRAPHIC.test(String.fromCodePoint(before)) &&
        PICTOGRAPHIC.test(String.fromCodePoint(after))
    );
}

function nameOf(codePoint) {
    for (const [first, last, name] of [...STRIPPED_RANGES, ...REPORTED_ONLY_RANGES]) {
        if (codePoint >= first && codePoint <= last) return name;
    }
    return codePoint === ZWJ ? "ZERO WIDTH JOINER" : "INCONNU";
}

/**
 * Retire les caracteres invisibles parasites d'une chaine.
 *
 * Preserve les espaces francaises et les sequences emoji (voir l'en-tete).
 * Retourne la valeur telle quelle si ce n'est pas une chaine, pour pouvoir
 * s'appliquer sans precaution sur un champ absent.
 */
export function stripInvisibleChars(value) {
    if (typeof value !== "string" || value === "") return value;

    return value.replace(STRIP_PATTERN, (match, offset, input) => {
        if (match.codePointAt(0) === ZWJ && joinsPictographic(input, offset)) {
            return match;
        }
        return "";
    });
}

/**
 * Liste les caracteres invisibles d'une chaine, sans rien modifier.
 *
 * Chaque entree porte `stripped: false` quand le caractere est signale mais
 * conserve (espace typographique, liant d'emoji), pour qu'un controle puisse
 * distinguer ce qui sera retire de ce qui demande seulement un coup d'oeil.
 */
export function findInvisibleChars(value) {
    if (typeof value !== "string" || value === "") return [];

    const found = [];
    let line = 1;
    let lastIndex = 0;

    for (const match of value.matchAll(DETECT_PATTERN)) {
        const index = match.index;
        const codePoint = match[0].codePointAt(0);

        for (let i = lastIndex; i < index; i += 1) {
            if (value[i] === "\n") line += 1;
        }
        lastIndex = index;

        const isEmojiJoiner = codePoint === ZWJ && joinsPictographic(value, index);
        const isReportedOnly = REPORTED_ONLY_RANGES.some(
            ([first, last]) => codePoint >= first && codePoint <= last,
        );

        found.push({
            codePoint,
            label: `U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`,
            name: isEmojiJoiner ? "ZERO WIDTH JOINER (sequence emoji)" : nameOf(codePoint),
            index,
            line,
            stripped: !isEmojiJoiner && !isReportedOnly,
        });
    }

    return found;
}

/**
 * Vrai si la chaine contient au moins un caractere que le nettoyage retirerait.
 */
export function hasInvisibleChars(value) {
    return findInvisibleChars(value).some((entry) => entry.stripped);
}

/**
 * Nettoie toutes les chaines d'une structure, en profondeur.
 *
 * Pour le JSON rendu par un modele : le texte a nettoyer n'y est pas en
 * surface, il est reparti dans les champs d'un objet imbrique (l'enonce d'une
 * etape, ses options, son explication). Nettoyer la chaine JSON brute avant de
 * la parser serait plus court mais faux : un caractere invisible peut se
 * trouver dans une sequence d'echappement, et le retirer casserait le parsing.
 *
 * Les cles ne sont pas touchees : ce sont des identifiants techniques, jamais
 * du texte affiche.
 */
export function stripInvisibleCharsDeep(value) {
    if (typeof value === "string") return stripInvisibleChars(value);
    if (Array.isArray(value)) return value.map(stripInvisibleCharsDeep);

    // Uniquement les objets simples : une Date ou une Map traversee ici en
    // ressortirait en objet nu.
    if (value !== null && typeof value === "object" && value.constructor === Object) {
        const cleaned = {};
        for (const [key, item] of Object.entries(value)) {
            cleaned[key] = stripInvisibleCharsDeep(item);
        }
        return cleaned;
    }

    return value;
}
